import { create } from 'zustand';
import type { TimerStateItem } from '@hygge/shared';
import { getCurrentTimer } from '@/lib/time-entries';

interface TimerStore {
  isRunning: boolean;
  startedAt: string | null;
  taskId: string | null;
  taskKey: string | null;
  taskTitle: string | null;
  projectId: string | null;
  projectName: string | null;
  elapsed: number; // seconds
  intervalId: number | null;
  load: () => Promise<void>;
  /**
   * Adopt a timer the server has confirmed is running: one just started, or one
   * found still running on load.
   *
   * Starting and stopping are the mutation hooks' job (`useStartTimer` /
   * `useStopTimer`). This store used to call those endpoints itself, which made
   * it a second write path that touched no query cache - so whichever list was
   * on screen kept showing the state from before the timer stopped until the
   * page was reloaded by hand.
   */
  applyTimer: (timer: TimerStateItem) => void;
  tick: () => void;
  reset: () => void;
}

export const useTimerStore = create<TimerStore>((set, get) => ({
  isRunning: false,
  startedAt: null,
  taskId: null,
  taskKey: null,
  taskTitle: null,
  projectId: null,
  projectName: null,
  elapsed: 0,
  intervalId: null,

  load: async () => {
    try {
      const timer = await getCurrentTimer();
      if (timer && timer.isRunning) {
        get().applyTimer(timer);
      } else {
        get().reset();
      }
    } catch {
      // Timer not available (extension disabled, etc.)
    }
  },

  applyTimer: (timer) => {
    set({
      isRunning: true,
      startedAt: timer.startedAt,
      taskId: timer.taskId,
      taskKey: timer.task?.key ?? null,
      taskTitle: timer.task?.title ?? null,
      projectId: timer.projectId,
      projectName: timer.project?.name ?? null,
      // Read off the server's own start time rather than assumed to be zero: a
      // timer adopted on load has been running for a while, and one just
      // started lands on the same arithmetic at about zero.
      elapsed: Math.floor((Date.now() - new Date(timer.startedAt).getTime()) / 1000),
    });
    // Start ticking
    const { intervalId: existingId } = get();
    if (existingId) clearInterval(existingId);
    const id = window.setInterval(() => get().tick(), 1000);
    set({ intervalId: id });
  },

  tick: () => {
    const { startedAt } = get();
    if (startedAt) {
      const elapsed = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
      set({ elapsed });
    }
  },

  reset: () => {
    const { intervalId } = get();
    if (intervalId) clearInterval(intervalId);
    set({
      isRunning: false,
      startedAt: null,
      taskId: null,
      taskKey: null,
      taskTitle: null,
      projectId: null,
      projectName: null,
      elapsed: 0,
      intervalId: null,
    });
  },
}));
